import { exists } from '@jack-henry/frontend-utils/functions';
import { TmBaseComponent } from '@treasury/presentation';
import { checkIcon, depositIcon } from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-bottom-sheet';
import '@treasury/presentation/components/tm-icon';
import '@treasury/presentation/components/tm-list';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { filterTableResults } from '../../../utilities/table-result-filter';
import { ResearchedTransactionVm } from '../data/researched-transaction-view-model';
import './account-research-transactions-detail-sheet';

const tagName = 'account-research-transactions-account-detail-sheet';
@customElement(tagName)
export class AccountResearchTransactionsAccountDetailSheet extends TmBaseComponent {
    @property()
    public open = false;

    @property()
    public transactions: ResearchedTransactionVm[] = [];

    @property()
    public transactionDateRange = '';

    @state()
    private filteredTransactions: ResearchedTransactionVm[] = [];

    @state()
    private transactionDetailOpen = false;

    @state()
    private selectedTransaction?: ResearchedTransactionVm;

    @state()
    private searchTerm = '';

    protected render() {
        return html` <account-research-transactions-detail-sheet
                .open=${this.transactionDetailOpen}
                .transaction=${this.selectedTransaction}
                @close=${() => {
                    this.transactionDetailOpen = false;
                    this.selectedTransaction = undefined;
                    this.open = true;
                    this.dispatchEvent(new CustomEvent('open'));
                }}
            ></account-research-transactions-detail-sheet>
            <tm-bottom-sheet
                .open=${this.open}
                @close=${() => {
                    this.open = false;
                    this.searchTerm = '';
                    this.dispatchEvent(new CustomEvent('close'));
                }}
            >
                ${this.transactions.length ? this.renderTransactions() : nothing}
            </tm-bottom-sheet>`;
    }

    protected updated(changedProps: Map<string, boolean>) {
        if (changedProps.has('open') && this.open) {
            this.filteredTransactions = this.transactions;
        }
    }

    private renderTransactions() {
        return html`<div slot="header-center">
                <div class="flex flex-col">
                    <div>${this.transactions[0].accountName}</div>
                    <div class="text-xs">(${this.transactions[0].accountNumber})</div>
                </div>
            </div>
            <div slot="header-right"></div>
            <div class="bg-primary">
                <tm-list-layout
                    disableStickyHeaders
                    .items=${this.filteredTransactions}
                    .virtualScrollItemThreshold=${10}
                    .groupBy=${'displayDate'}
                    .headerTitle=${'Transactions'}
                    .searchFields=${[
                        'accountDisplayLabel',
                        'description',
                        'displayAmount',
                        'displayCode',
                    ]}
                    .searchPlaceholder=${'Search'}
                    .columns=${[
                        {
                            renderer: (transaction: ResearchedTransactionVm) => {
                                return html`<div class="flex flex-col">
                                    <span class="text-sm font-medium overflow-hidden text-ellipsis"
                                        >${transaction.description}${transaction.checkNumber
                                            ? html` | ${transaction.checkNumber}`
                                            : nothing}</span
                                    ><span class="text-xs text-secondary"
                                        >${transaction.accountDisplayLabel} |
                                        ${transaction.transactionCodeDescription}
                                        ${transaction.checkImageNumber
                                            ? html`<tm-icon
                                                  class="pl-2 mt-[-10px]"
                                                  .icon=${checkIcon}
                                              ></tm-icon>`
                                            : nothing}
                                        ${transaction.isDeposit
                                            ? html`<tm-icon
                                                  class="pl-2 mt-[-10px]"
                                                  .icon=${depositIcon}
                                              ></tm-icon>`
                                            : nothing}
                                    </span>
                                </div>`;
                            },
                            options: { shrink: 0 },
                        },
                        {
                            renderer: (transaction: ResearchedTransactionVm) => {
                                return html`<div class="flex flex-col">
                                    <span class="text-sm font-bold"
                                        >${transaction.displayAmount}</span
                                    >
                                </div>`;
                            },
                            options: { shrink: 1, textAlign: 'end' },
                        },
                    ]}
                    @select-list-item=${({ detail: transaction }: CustomEvent) => {
                        if (exists(transaction)) {
                            this.selectedTransaction = transaction;
                            this.transactionDetailOpen = true;
                            this.open = false;
                            this.dispatchEvent(new CustomEvent('close'));
                        }
                    }}
                >
                    <div slot="header-subtitle">${this.renderHeaderSubtitle()}</div>
                </tm-list-layout>
            </div>`;
    }

    private renderHeaderSubtitle() {
        return html`<div class="flex items-center">
            <div class="flex-grow text-xs text-secondary">${this.transactionDateRange}</div>
        </div>`;
    }

    private filterTransactions() {
        this.filteredTransactions = filterTableResults(this.searchTerm, this.transactions, [
            'accountDisplayLabel',
            'description',
            'displayAmount',
            'displayCode',
        ]);
    }

    public static get styles() {
        return [
            css`
                tm-list-layout {
                    --grid-height: 100dvh;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: AccountResearchTransactionsAccountDetailSheet;
    }
}
